import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translations
const translations = {
  sk: {
    // Navigation
    home: 'Domov',
    recipes: 'Recepty',
    aiGenerator: 'Vymyslieť recept',
    favorites: 'Obľúbené',
    profile: 'Profil',
    login: 'Prihlásiť sa',
    logout: 'Odhlásiť sa',
    
    // Hero
    heroTitle: 'Varíme srdcom',
    heroSubtitle: 'Objavte radosť z varenia s Luciou',
    heroDescription: 'Stovky receptov, ktoré sú CHUTNÉ, ZDRAVÉ, RÝCHLE, DOSTUPNÉ a JEDNODUCHÉ.',
    getStarted: 'Objavte recepty',
    
    // Magic Five
    magicFive: 'Magická päťka',
    tasty: 'Chutne',
    healthy: 'Zdravo',
    quick: 'Rýchlo',
    affordable: 'Dostupne',
    simple: 'Jednoducho',
    
    // Filters
    filters: 'Filtre',
    mealType: 'Typ jedla',
    season: 'Sezóna',
    category: 'Kategória',
    difficulty: 'Obtiažnosť',
    search: 'Hľadať recept...',
    clearFilters: 'Vymazať filtre',
    
    // Recipe
    ingredients: 'Ingrediencie',
    instructions: 'Postup',
    prepTime: 'Príprava',
    cookTime: 'Varenie',
    servings: 'Porcie',
    minutes: 'min',
    portions: 'porcií',
    tipFromLucia: 'Tip od Lucie',
    addToFavorites: 'Pridať do obľúbených',
    removeFromFavorites: 'Odstrániť z obľúbených',
    
    // AI Generator
    aiTitle: 'Nechajte sa inšpirovať',
    aiDescription: 'Popíšte, čo by ste chceli uvariť, alebo zadajte ingrediencie, ktoré máte doma.',
    aiPlaceholder: 'Napr. "Chcem niečo rýchle z kuracieho mäsa" alebo "Mám doma tekvicu a syr"',
    generate: 'Vymyslieť recept',
    generating: 'Premýšľam...',
    
    // Auth
    loginWithGoogle: 'Prihlásiť sa cez Google',
    welcomeBack: 'Vitajte späť',
    
    // General
    loading: 'Načítavam...',
    noRecipes: 'Žiadne recepty nenájdené',
    error: 'Nastala chyba',
    saveRecipe: 'Uložiť recept',
    
    // Footer
    footerText: '© 2024 S Luciou v kuchyni. Všetky práva vyhradené.',
    clubOfWomen: 'Klub žien, čo varia srdcom'
  },
  cz: {
    // Navigation
    home: 'Domů',
    recipes: 'Recepty',
    aiGenerator: 'Vymyslet recept',
    favorites: 'Oblíbené',
    profile: 'Profil',
    login: 'Přihlásit se',
    logout: 'Odhlásit se',
    
    // Hero
    heroTitle: 'Vaříme srdcem',
    heroSubtitle: 'Objevte radost z vaření s Lucií',
    heroDescription: 'Stovky receptů, které jsou CHUTNÉ, ZDRAVÉ, RYCHLÉ, DOSTUPNÉ a JEDNODUCHÉ.',
    getStarted: 'Objevte recepty',
    
    // Magic Five
    magicFive: 'Magická pětka',
    tasty: 'Chutně',
    healthy: 'Zdravě',
    quick: 'Rychle',
    affordable: 'Dostupně',
    simple: 'Jednoduše',
    
    // Filters
    filters: 'Filtry',
    mealType: 'Typ jídla',
    season: 'Sezóna',
    category: 'Kategorie',
    difficulty: 'Obtížnost',
    search: 'Hledat recept...',
    clearFilters: 'Vymazat filtry',
    
    // Recipe
    ingredients: 'Ingredience',
    instructions: 'Postup',
    prepTime: 'Příprava',
    cookTime: 'Vaření',
    servings: 'Porce',
    minutes: 'min',
    portions: 'porcí',
    tipFromLucia: 'Tip od Lucie',
    addToFavorites: 'Přidat do oblíbených',
    removeFromFavorites: 'Odebrat z oblíbených',
    
    // AI Generator
    aiTitle: 'Nechte se inspirovat',
    aiDescription: 'Popište, co byste chtěli uvařit, nebo zadejte ingredience, které máte doma.',
    aiPlaceholder: 'Např. "Chci něco rychlého z kuřecího masa" nebo "Mám doma dýni a sýr"',
    generate: 'Vymyslet recept',
    generating: 'Přemýšlím...',
    
    // Auth
    loginWithGoogle: 'Přihlásit se přes Google',
    welcomeBack: 'Vítejte zpět',
    
    // General
    loading: 'Načítám...',
    noRecipes: 'Žádné recepty nenalezeny',
    error: 'Nastala chyba',
    saveRecipe: 'Uložit recept',
    
    // Footer
    footerText: '© 2024 S Lucií v kuchyni. Všechna práva vyhrazena.',
    clubOfWomen: 'Klub žen, co vaří srdcem'
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguageState] = useState('sk');

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        if (userData.language) {
          setLanguageState(userData.language);
        }
      }
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    window.location.href = '/';
  };

  const setLanguage = async (lang) => {
    setLanguageState(lang);
    if (user) {
      try {
        await fetch(`${API_URL}/api/auth/language`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ language: lang })
        });
      } catch (error) {
        console.error('Language update error:', error);
      }
    }
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.sk[key] || key;
  };

  const getLocalizedField = (item, field) => {
    if (!item) return '';
    if (language === 'cz' && item[`${field}_cz`]) {
      return item[`${field}_cz`];
    }
    return item[field] || '';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      loading, 
      login, 
      logout, 
      language, 
      setLanguage, 
      t,
      getLocalizedField,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};
